/**
 * useHomeNeeds — the one place the Needs stream is assembled (plan 443).
 *
 * Both the header's Home badge (`useHomeNeedsCount`) and the Home page's
 * Needs list read this hook, so the count and the rows come from the same
 * function over the same stores and cannot disagree. It also reports which of
 * the reads Home issues have resolved or failed, for the page's settle rule.
 *
 * Marketplace inputs load only while the go-live gate is `on`; with it `off` or
 * `loading` the requests list and the Home summary are `null` and no
 * marketplace request is issued (the Today store's merged requests summary
 * included, via `setTodayMarketplaceReads`).
 *
 * The stream is built against a clock that ticks once a minute (pinned under
 * IS_PREVIEW), so urgency and overdue follow the real time on a page or header
 * left open (a follow-up due yesterday turns overdue at Pacific midnight).
 * Every caller gets the same clock behaviour, so the header badge and Home's
 * list are always built from identical inputs; `now` is returned for Home's
 * own chips and greeting to read the same instant.
 */
import { useEffect, useMemo, useState } from "react";
import type { MarketplaceConnectionRequest } from "../lib/api";
import { buildNeedsStream, type NeedsStreamRow } from "../lib/needs-stream";
import { homeNow } from "../lib/pro-home-preview";
import { IS_PREVIEW } from "../lib/pro-mode";
import type { ProHomeSummary } from "../lib/pro-home-types";
import type { TodayQueueVM } from "../lib/view-model";
import { type SummarySlice, useProHome } from "../stores/proHome";
import { setTodayMarketplaceReads, useTodayData, useVisibleTodayQueue } from "../stores/today";
import type { MarketplaceGate } from "./useMarketplaceGate";
import { useMarketplaceRequestListState } from "./useMarketplaceRequestList";

/** Deadline chips, urgency and the greeting are read against this clock; a minute keeps "in 20m" honest. */
const CLOCK_TICK_MS = 60_000;

/** Home's clock: ticks every minute, except under IS_PREVIEW where fixtures pin it. */
function useHomeClock(): Date {
  const [now, setNow] = useState(() => homeNow());
  useEffect(() => {
    if (IS_PREVIEW) return;
    const id = setInterval(() => setNow(homeNow()), CLOCK_TICK_MS);
    return () => clearInterval(id);
  }, []);
  return now;
}

export interface HomeNeeds {
  /** The instant the stream was built against; ticks each minute. */
  now: Date;
  rows: NeedsStreamRow[];
  /** `rows.length`; the header badge. */
  count: number;
  /** Visible Today queue (in-flight done/snooze applied). */
  today: TodayQueueVM;
  /** The Today read resolved (or failed): the queue is meaningful. */
  todayLoaded: boolean;
  requests: MarketplaceConnectionRequest[] | null;
  summary: ProHomeSummary | null;
  home: SummarySlice<ProHomeSummary>;
  pipeline: ReturnType<typeof useProHome>["pipeline"];
  /**
   * Every read Home issues for this gate has resolved or failed. False while
   * the gate is `loading`. Reads that are not issued (marketplace ones when the
   * gate is `off`) do not hold it up.
   */
  settled: boolean;
}

const done = (slice: SummarySlice<unknown>) => slice.hasLoaded || slice.error !== null;

export interface HomeNeedsOptions {
  /**
   * Load the pipeline summary (default true: Home). The header passes false: its
   * badge and the paused banner need only the Home summary, and it is mounted on
   * every route.
   */
  pipeline?: boolean;
}

export function useHomeNeeds(
  gate: MarketplaceGate,
  { pipeline: wantsPipeline = true }: HomeNeedsOptions = {},
): HomeNeeds {
  const marketplaceOn = gate === "on";
  const now = useHomeClock();
  // Declared before `useTodayData` so the Today store knows the gate before its
  // own load runs: with the gate not `on` its queue read skips the marketplace
  // requests summary (the Today store is CRM data plus that one merged read).
  useEffect(() => setTodayMarketplaceReads(marketplaceOn), [marketplaceOn]);
  // Loads the Today queue; the visible view applies in-flight done/snooze.
  const todayData = useTodayData();
  const today = useVisibleTodayQueue();
  const requestList = useMarketplaceRequestListState(marketplaceOn);
  const { home, pipeline } = useProHome(gate, { pipeline: wantsPipeline });
  const summary = home.data;
  const requests = requestList.rows;

  const stream = useMemo(
    () => buildNeedsStream({ today, requests, summary, now }),
    [today, requests, summary, now],
  );

  const todayLoaded = todayData.hasLoaded || todayData.error !== null;
  const settled =
    gate !== "loading" &&
    todayLoaded &&
    (!wantsPipeline || done(pipeline)) &&
    (!marketplaceOn || (done(home) && requestList.settled));

  return {
    now,
    rows: stream.rows,
    count: stream.count,
    today,
    todayLoaded,
    requests,
    summary,
    home,
    pipeline: pipeline,
    settled,
  };
}
